import { useLocation, useNavigate } from 'react-router-dom';
import {
  buildHash,
  buildPageUrl,
  buildQueryString,
  buildSidePathUrl,
  mergeQuery,
  normalizeSidePath,
  parseHash,
  parseSearch,
} from './routeUrl';
import type { SidePathOptions } from './routeUrl';

// Route state and changes interfaces
interface RouteState {
  mainPath: string;
  mainQuery: { [key: string]: string };
  sidebarPath: string;
  sidebarQuery: { [key: string]: string };
}

interface RouteChanges {
  mainPath?: string;
  mainQuery?: { [key: string]: string | number | boolean };
  sidebarPath?: string;
  sidebarQuery?: { [key: string]: string | number | boolean };
}

const useRoute = () => {
  const { pathname, hash, search } = useLocation();
  const navigation = useNavigate();

  // Parse the hash for sidebar navigation
  const sidebar = parseHash(hash);

  // Parse the search string for query parameters
  const query = parseSearch(search);

  // Current route state
  const currentState: RouteState = {
    mainPath: pathname,
    mainQuery: query,
    sidebarPath: `/${sidebar.cleanPath}`,
    sidebarQuery: sidebar.query,
  };

  const safeNavigate = (target: string) => {
    const current = window.location.pathname + window.location.search + window.location.hash;
    if (target !== current) {
      console.log('Navigating to:', target);
      navigation(target);
    }
  };

  // Individual navigation methods
  const navMainPath = (newPath: string) => {
    const normalizedPath = newPath.startsWith('/') ? newPath : `/${newPath}`;
    const hashStr = buildHash(sidebar.cleanPath, sidebar.query);
    navigation(normalizedPath + buildQueryString(query) + hashStr);
  };

  // Opens a new page. Drops the current main query and the current sidebar.
  // The URL can hold its own query and sidebar: '/broker/x/seasons#/season/1'.
  const navPage = (url: string) => {
    navigation(buildPageUrl(url));
  };

  // A null value removes its key. `replace` changes the current history entry instead of adding one.
  const navMainQuery = (
    newQuery: { [key: string]: string | number | boolean | null },
    options?: { replace?: boolean },
  ) => {
    const hashStr = buildHash(sidebar.cleanPath, sidebar.query);
    navigation(pathname + buildQueryString(mergeQuery(query, newQuery)) + hashStr, { replace: options?.replace });
  };

  // Keeps the current sidebar query unless options.resetQuery is true.
  // An empty path closes the sidebar.
  const navSidePath = (
    newSidePath: string,
    options?: SidePathOptions,
  ): Promise<void> => {
    return new Promise((resolve) => {
      navigation(buildSidePathUrl({ pathname, search, hash }, newSidePath, options));
      resolve();
    });
  };

  const navSideQuery = (
    newSideQuery: { [key: string]: string | number | boolean },
  ) => {
    const mergedSideQuery = { ...sidebar.query, ...newSideQuery };
    const cleanSidebarPath = normalizeSidePath(sidebar.cleanPath);
    const newHash = buildHash(cleanSidebarPath, mergedSideQuery);
    console.trace('navSideQuery CALLED', newSideQuery);
    safeNavigate(pathname + search + newHash);
  };

  // Route reducer
  const navReducer = (changes: RouteChanges) => {
    const newState = { ...currentState };

    if (changes.mainPath !== undefined) {
      newState.mainPath = changes.mainPath.startsWith('/')
        ? changes.mainPath
        : `/${changes.mainPath}`;
    }

    if (changes.mainQuery !== undefined) {
      newState.mainQuery = {
        ...newState.mainQuery,
        ...(changes.mainQuery as Record<string, string>),
      };
    }

    if (changes.sidebarPath !== undefined) {
      const cleanSidebarPath = normalizeSidePath(changes.sidebarPath);
      newState.sidebarPath = `/${cleanSidebarPath}`;
    }

    if (changes.sidebarQuery !== undefined) {
      newState.sidebarQuery = {
        ...newState.sidebarQuery,
        ...(changes.sidebarQuery as Record<string, string>),
      };
    }

    const mainQueryString = buildQueryString(newState.mainQuery);
    const cleanSidebarPath = normalizeSidePath(newState.sidebarPath);
    const newHash = buildHash(cleanSidebarPath, newState.sidebarQuery);

    navigation(newState.mainPath + mainQueryString + newHash);
  };

  return {
    mainPath: pathname,
    mainQuery: query,
    sidebarPath: `/${sidebar.cleanPath}`,
    sidebarQuery: sidebar.query,

    navMainPath,
    navMainQuery,
    navPage,
    navSidePath,
    navSideQuery,
    navReducer,
  };
};

export default useRoute;